import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import classNames from "classnames";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import Icon from "@material-ui/core/Icon";

import api from "api/index";

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "220px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #0d1b6e 0%, #1a237e 60%, #1565c0 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "30px 40px",
    display: "flex",
    flexDirection: "column",
    color: "#fff"
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    color: "rgba(255,255,255,0.8)",
    textDecoration: "none",
    fontSize: "13px",
    marginBottom: "20px",
    cursor: "pointer",
    transition: "color 0.2s",
    "&:hover": { color: "#fff" }
  },
  heroTitle: { fontSize: "28px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "14px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400 },
  
  // KPI stat cards
  kpiRow: { display: "flex", gap: "16px", marginTop: "-80px", marginBottom: "32px", padding: "0 20px", zIndex: 3, position: "relative", flexWrap: "nowrap", overflowX: "auto" },
  statCard: {
    flex: 1,
    minWidth: "200px",
    padding: "20px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    border: "1px solid #f1f5f9",
  },
  statHeader: { display: "flex", alignItems: "center", gap: "12px" },
  iconBox: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNumber: { fontSize: "24px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "12px", color: "#64748b", margin: 0, fontWeight: 500 },
  statMeta: { fontSize: "11px", color: "#94a3b8", marginTop: -4 },

  filterCard: {
    background: "#fff",
    borderRadius: "16px",
    border: "1px solid #f1f5f9",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
  },
  filterGrid: { display: "flex", gap: "24px", alignItems: "flex-end" },
  filterItem: { flex: 1 },
  filterLabel: { display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "8px" },
  selectInput: {
    width: "100%",
    height: "42px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "0 12px",
    fontSize: "14px",
    color: "#1e293b",
    outline: "none",
    "&:focus": { borderColor: "#3b82f6", background: "#fff" }
  },
  passMarkInput: {
    height: "42px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "0 12px",
    fontSize: "14px",
    width: "100px",
    textAlign: "center"
  },

  tableActions: { display: "flex", gap: "8px" },
  actionBtn: {
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
    textTransform: "none",
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  saveBtn: { background: "#2563eb", color: "#fff", "&:hover": { background: "#1d4ed8" } },
  outlineBtn: { border: "1px solid #e2e8f0", background: "#fff", color: "#475569", "&:hover": { background: "#f8fafc" } },

  errorMsg: { color: "#ef4444", background: "#fee2e2", padding: "12px", borderRadius: "8px", marginBottom: "16px", fontSize: "13px" },
  successMsg: { color: "#10b981", background: "#d1fae5", padding: "12px", borderRadius: "8px", marginBottom: "16px", fontSize: "13px" },
  
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    textAlign: "center"
  },
  emptyIcon: { fontSize: "64px", color: "#e2e8f0", marginBottom: "16px" },
  emptyTitle: { fontSize: "16px", fontWeight: 600, color: "#1e293b", margin: "0 0 8px" },
  emptyText: { fontSize: "14px", color: "#64748b", margin: "0 0 24px", maxWidth: "300px" }
};

const GRADE_TONE = { A: "success", B: "info", C: "muted", D: "warning", E: "danger", F: "danger" };

class ExamResults extends Component {
  state = {
    courses: [],
    semesters: [],
    selectedCourse: "",
    selectedSemester: "",
    results: [],
    loading: false,
    approving: false,
    rejectOpen: false,
    rejectReason: "",
    error: null,
    success: null,
    passMark: "",
    passMarkSaving: false
  };

  componentDidMount() {
    this.fetchMeta();
    this.fetchPassMark();
  }

  fetchMeta = async () => {
    try {
      const [cRes, sRes] = await Promise.all([
        api.get("/courses?limit=100"),
        api.get("/semesters?limit=100")
      ]);
      const cd = cRes.data.data || cRes.data;
      const sd = sRes.data.data || sRes.data;
      const semesters = Array.isArray(sd) ? sd : sd.data || [];
      const current = semesters.find(s => s.isCurrent);
      this.setState({
        courses: Array.isArray(cd) ? cd : cd.data || [],
        semesters,
        selectedSemester: current ? current.id : ""
      });
    } catch (e) { /* silent */ }
  };

  fetchPassMark = async () => {
    try {
      const res = await api.get("/settings/min-pass-mark");
      const d = res.data.data || res.data;
      this.setState({ passMark: d.value });
    } catch (e) { /* silent */ }
  };

  handleSavePassMark = async () => {
    this.setState({ passMarkSaving: true, error: null });
    try {
      await api.put("/settings/min-pass-mark", { value: this.state.passMark });
      this.setState({ passMarkSaving: false, success: "Minimum pass mark updated." });
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to update pass mark.", passMarkSaving: false });
    }
  };

  fetchResults = async () => {
    const { selectedCourse, selectedSemester } = this.state;
    if (!selectedCourse) return;
    this.setState({ loading: true, error: null });
    try {
      const params = { courseId: selectedCourse, limit: 100 };
      if (selectedSemester) params.semesterId = selectedSemester;
      const res = await api.get("/results", { params });
      const d = res.data.data || res.data;
      this.setState({ results: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) { this.setState({ loading: false }); }
  };

  handleApproveAll = async () => {
    const { results, selectedCourse, selectedSemester } = this.state;
    const pending = results.filter(r => r.status !== "final");
    if (!pending.length) { this.setState({ success: "All results are already final." }); return; }
    if (!window.confirm(`Approve and finalize ${pending.length} result(s)?`)) return;
    this.setState({ approving: true, error: null });
    try {
      const res = await api.post("/results/approve-bulk", { courseId: selectedCourse, semesterId: selectedSemester });
      const d = res.data.data || res.data;
      const repeatNote = d.repeatRetakes ? ` (${d.repeatRetakes} of them repeat retakes)` : "";
      const retakeNote = d.flaggedForRetake ? ` ${d.flaggedForRetake} student(s) flagged for retake${repeatNote}.` : "";
      this.setState({ approving: false, success: `Results approved and finalized.${retakeNote}` });
      this.fetchResults();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Approval failed.", approving: false });
    }
  };

  handleApproveOne = async (id) => {
    try {
      const res = await api.patch(`/results/${id}/approve`);
      const { retakeFlagged, retakeAttemptNumber } = res.data;
      this.setState({
        success: retakeFlagged
          ? `Result finalized. Student flagged for retake (attempt #${retakeAttemptNumber}).`
          : "Result finalized."
      });
      this.fetchResults();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Approval failed." });
    }
  };

  render() {
    const { classes } = this.props;
    const {
      courses, semesters, selectedCourse, selectedSemester, results, loading, approving, error, success,
      passMark, passMarkSaving
    } = this.state;

    const pending = results.filter(r => r.status !== "final");
    const approvedCount = results.filter(r => r.status === "final").length;
    const failingCount = results.filter(r => {
      const g = r.grade;
      return g === "F" || g === "E";
    }).length;
    const completionRate = results.length > 0 ? Math.round((approvedCount / results.length) * 100) : 0;

    const columns = [
      { id: "studentNo", header: "STD NO", accessorFn: r => r.enrollment?.student?.studentNo || "—", meta: { variant: "mono" } },
      {
        id: "student",
        header: "STUDENT",
        accessorFn: r => `${r.enrollment?.student?.firstName || ""} ${r.enrollment?.student?.lastName || ""}`.trim() || "—"
      },
      { id: "cw", header: "CW (30%)", accessorFn: r => (r.courseworkScore != null ? `${r.courseworkScore}%` : "—") },
      { id: "exam", header: "EXAM (70%)", accessorFn: r => (r.examScore != null ? `${r.examScore}%` : "—") },
      { id: "total", header: "TOTAL", accessorFn: r => (r.totalScore != null ? `${r.totalScore}%` : "—") },
      {
        id: "grade",
        header: "GRADE",
        cell: info => {
          const grade = info.row.original.grade;
          return grade ? <Pill tone={GRADE_TONE[grade] || "muted"}>{grade}</Pill> : "—";
        }
      },
      {
        id: "status",
        header: "STATUS",
        cell: info => {
          const isFinal = info.row.original.status === "final";
          return <Pill tone={isFinal ? "success" : "warning"}>{isFinal ? "Final" : "Provisional"}</Pill>;
        }
      },
      {
        id: "actions",
        header: "ACTIONS",
        cell: info => {
          const r = info.row.original;
          return r.status !== "final" ? (
            <Button size="sm" color="success" simple onClick={() => this.handleApproveOne(r.id)}>
              Approve
            </Button>
          ) : (
            <Icon style={{ color: "#10b981", fontSize: "20px" }}>check_circle</Icon>
          );
        }
      }
    ];

    return (
      <div style={{ padding: "0 0 40px" }}>
        <div className={classes.heroWrap}>
          <div className={classes.heroOverlay} />
          <div className={classes.heroContent}>
            <div className={classes.backLink} onClick={() => this.props.history.goBack()}>
              <Icon fontSize="small" style={{ marginRight: "4px" }}>arrow_back</Icon> Back to Exam Results
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <div>
                <h1 className={classes.heroTitle}>Exam Results Approval</h1>
                <p className={classes.heroSub}>Review provisional results and finalize for official records.</p>
              </div>
              <Button 
                className={classNames(classes.actionBtn, classes.saveBtn)} 
                style={{ background: "#fff", color: "#1a237e", padding: "12px 24px" }}
                onClick={this.handleApproveAll}
                disabled={approving || !pending.length}
              >
                {approving ? <CircularProgress size={20} color="inherit" /> : <><Icon>check_circle_outline</Icon> Approve All ({pending.length} Pending)</>}
              </Button>
            </div>
          </div>
        </div>

        <div className={classes.kpiRow}>
          <div className={classes.statCard}>
            <div className={classes.statHeader}>
              <div className={classes.iconBox} style={{ background: "#f5f3ff", color: "#8b5cf6" }}>
                <Icon>group</Icon>
              </div>
              <div>
                <h3 className={classes.statNumber}>{results.length}</h3>
                <p className={classes.statLabel}>Total Students</p>
              </div>
            </div>
            <p className={classes.statMeta}>Enrolled</p>
          </div>

          <div className={classes.statCard}>
            <div className={classes.statHeader}>
              <div className={classes.iconBox} style={{ background: "#ecfdf5", color: "#10b981" }}>
                <Icon>assignment_turned_in</Icon>
              </div>
              <div>
                <h3 className={classes.statNumber}>{approvedCount}</h3>
                <p className={classes.statLabel}>Results Entered</p>
              </div>
            </div>
            <p className={classes.statMeta}>{completionRate}% of total</p>
          </div>

          <div className={classes.statCard}>
            <div className={classes.statHeader}>
              <div className={classes.iconBox} style={{ background: "#fffbeb", color: "#f59e0b" }}>
                <Icon>schedule</Icon>
              </div>
              <div>
                <h3 className={classes.statNumber}>{pending.length}</h3>
                <p className={classes.statLabel}>Pending Approval</p>
              </div>
            </div>
            <p className={classes.statMeta}>{pending.length} results</p>
          </div>

          <div className={classes.statCard}>
            <div className={classes.statHeader}>
              <div className={classes.iconBox} style={{ background: "#eff6ff", color: "#3b82f6" }}>
                <Icon>verified</Icon>
              </div>
              <div>
                <h3 className={classes.statNumber}>{approvedCount}</h3>
                <p className={classes.statLabel}>Approved</p>
              </div>
            </div>
            <p className={classes.statMeta}>{approvedCount} results</p>
          </div>

          <div className={classes.statCard}>
            <div className={classes.statHeader}>
              <div className={classes.iconBox} style={{ background: "#fef2f2", color: "#ef4444" }}>
                <Icon>flag</Icon>
              </div>
              <div>
                <h3 className={classes.statNumber}>{failingCount}</h3>
                <p className={classes.statLabel}>Requires Attention</p>
              </div>
            </div>
            <p className={classes.statMeta}>{failingCount} results</p>
          </div>
        </div>

        <div style={{ padding: "0 20px" }}>
          <div className={classes.filterCard}>
            <div className={classes.filterGrid}>
              <div className={classes.filterItem}>
                <label className={classes.filterLabel}>Course</label>
                <select 
                  className={classes.selectInput}
                  value={selectedCourse} 
                  onChange={e => this.setState({ selectedCourse: e.target.value }, this.fetchResults)}
                >
                  <option value="">— Select Course —</option>
                  {courses.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
                </select>
              </div>
              <div className={classes.filterItem}>
                <label className={classes.filterLabel}>Semester</label>
                <select 
                  className={classes.selectInput}
                  value={selectedSemester} 
                  onChange={e => this.setState({ selectedSemester: e.target.value }, this.fetchResults)}
                >
                  <option value="">— All Semesters —</option>
                  {semesters.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.isCurrent ? " (Current)" : ""} — {s.academicYear?.name || ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className={classes.filterItem} style={{ flex: "0 0 auto" }}>
                <label className={classes.filterLabel}>Minimum Pass Mark</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input 
                    type="number"
                    className={classes.passMarkInput}
                    value={passMark}
                    onChange={e => this.setState({ passMark: e.target.value })}
                  />
                  <Button 
                    className={classNames(classes.actionBtn, classes.saveBtn)}
                    onClick={this.handleSavePassMark}
                    disabled={passMarkSaving}
                  >
                    {passMarkSaving ? <CircularProgress size={16} color="inherit" /> : "Save"}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {error && <div className={classes.errorMsg}>⚠ {error}</div>}
          {success && <div className={classes.successMsg}>✓ {success}</div>}

          <DataTable
            columns={columns}
            data={results}
            isLoading={loading}
            toolbar={
              <div className={classes.tableActions}>
                <Button className={classNames(classes.actionBtn, classes.outlineBtn)}>
                  <Icon fontSize="small">download</Icon> Export
                </Button>
                <Button className={classNames(classes.actionBtn, classes.outlineBtn)}>
                  <Icon fontSize="small">settings</Icon> Column Options
                </Button>
              </div>
            }
            emptyState={
              <div className={classes.emptyState}>
                <Icon className={classes.emptyIcon}>assignment_late</Icon>
                <h4 className={classes.emptyTitle}>No results found.</h4>
                <p className={classes.emptyText}>Enter results for students to review and approve.</p>
                <Button 
                  className={classNames(classes.actionBtn, classes.outlineBtn)}
                  onClick={() => this.props.history.push("/bwillis/results/coursework")}
                >
                  <Icon fontSize="small" style={{ marginRight: "8px" }}>edit</Icon> Enter Results
                </Button>
              </div>
            }
          />
        </div>
      </div>
    );
  }
}

ExamResults.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(ExamResults));
